<script setup>
import { h, ref, computed } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useBranding } from 'shared/composables/useBranding';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import LordIconIcon from 'next/icon/LordIconIcon.vue';
import { LORDICON } from 'dashboard/constants/lordicons';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { TEAM_SUPERVISOR } from 'dashboard/constants/permissions.js';
import Dialog from 'next/dialog/Dialog.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openKeyShortcutModal', 'enterReorderMode']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const { checkPermissions } = usePolicy();
const { replaceInstallationName } = useBranding();

const logoutDialogRef = ref(null);

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const lordIcon = (srcKey, fallback = '') => {
  const cfg = LORDICON[srcKey] || {};
  return h(LordIconIcon, {
    src: cfg.idle,
    srcActive: cfg.active,
    family: cfg.family || 'system',
    fallbackLucide: fallback,
    size: 16,
  });
};

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: lordIcon('support', 'i-lucide-life-buoy'),
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: lordIcon('keyboard', 'i-lucide-keyboard'),
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: lordIcon('agent', 'i-lucide-user-pen'),
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: lordIcon('theme', 'i-lucide-palette'),
      click: () => {
        document
          .querySelector('ninja-keys')
          ?.open?.({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.REORDER_SIDEBAR'),
      icon: 'i-lucide-arrow-up-down',
      click: () => {
        emit('enterReorderMode');
      },
    },
    // WA-fidelity: itens administrativos (Kanban/Contatos/Relatórios/Ao Vivo/
    // Campanhas/Central de Ajuda/Configurações) ficam apenas no rail principal
    // — não duplicar no menu de perfil para evitar redundância.
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: lordIcon('helpCenter', 'i-lucide-book'),
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: lordIcon('changelog', 'i-lucide-scroll-text'),
      link: 'https://www.chatwoot.com/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      // Quem supervisiona time não administra a instalação. Esconder aqui é só a
      // metade visível: SuperAdmin::ApplicationController recusa a URL /super_admin.
      show:
        currentUser.value.type === 'SuperAdmin' &&
        !checkPermissions([TEAM_SUPERVISOR]),
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: lordIcon('superAdmin', 'i-lucide-castle'),
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: lordIcon('logout', 'i-lucide-power'),
      click: () => logoutDialogRef.value?.open(),
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        v-tooltip.right="isCollapsed ? currentUser.available_name : undefined"
        class="flex gap-2 items-center p-1 text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="[
          { 'bg-n-alpha-1': isOpen },
          isCollapsed ? 'justify-center' : 'w-full',
        ]"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div v-if="!isCollapsed" class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="min-w-0 truncate text-xs text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody class="bottom-12 z-50 mb-2 w-80 ltr:left-0 rtl:right-0">
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
  <!-- Fora do DropdownBody: o menu fecha ao clicar no item e levaria o diálogo junto. -->
  <Dialog
    ref="logoutDialogRef"
    type="alert"
    width="md"
    :title="t('SIDEBAR_ITEMS.LOGOUT_CONFIRM.TITLE')"
    :description="
      replaceInstallationName(t('SIDEBAR_ITEMS.LOGOUT_CONFIRM.DESCRIPTION'))
    "
    :confirm-button-label="t('SIDEBAR_ITEMS.LOGOUT_CONFIRM.CONFIRM')"
    :cancel-button-label="t('SIDEBAR_ITEMS.LOGOUT_CONFIRM.CANCEL')"
    @confirm="Auth.logout"
  />
</template>
